import { useLocation } from '@tanstack/react-router'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { PERMISSION_GECKO_INTERNAL_MEMBER } from 'types'

interface EntryBrowserSidebarContextValue {
  isOpen: boolean
  openSidebar: () => void
  closeSidebar: () => void
  toggleSidebar: () => void
}

const EntryBrowserSidebarCtx =
  createContext<EntryBrowserSidebarContextValue | null>(null)

export const useEntryBrowserSidebar = (): EntryBrowserSidebarContextValue => {
  const ctx = useContext(EntryBrowserSidebarCtx)
  if (!ctx)
    throw new Error(
      'useEntryBrowserSidebar must be used within EntryBrowserSidebarProvider'
    )
  return ctx
}

export const EntryBrowserSidebarProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { hasPermissions } = useGeckoPermissions()
  const isInternalUser = hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER])

  const openSidebar = useCallback(() => {
    if (isInternalUser) setIsOpen(true)
  }, [isInternalUser])

  const closeSidebar = useCallback(() => setIsOpen(false), [])
  const toggleSidebar = useCallback(() => {
    if (isInternalUser) setIsOpen((prev) => !prev)
  }, [isInternalUser])

  useEffect(() => {
    if (!isInternalUser) return

    const handler = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      const metaPressed = isMac ? e.metaKey : e.ctrlKey

      // Cmd+K belongs to the app-wide command palette.
      if (metaPressed && e.shiftKey && e.key.toLowerCase() === 'k') {
        if (location.pathname.startsWith('/entry-browser')) {
          return
        }

        e.preventDefault()
        e.stopPropagation()
        if (e.repeat) return
        setIsOpen((prev) => !prev)
      }
    }

    document.addEventListener('keydown', handler, true)
    return () => document.removeEventListener('keydown', handler, true)
  }, [location.pathname, isInternalUser])

  const value = useMemo(
    () => ({ isOpen, openSidebar, closeSidebar, toggleSidebar }),
    [isOpen, openSidebar, closeSidebar, toggleSidebar]
  )

  return (
    <EntryBrowserSidebarCtx.Provider value={value}>
      {children}
    </EntryBrowserSidebarCtx.Provider>
  )
}
